import {
    EventMessage,
    EventType,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import { B2CCustomPolicyRoutes } from '@copilot/data/config/routes';
import { AuthorizationManager } from './authorization';
import isNil from 'lodash/isNil';
import { isString, get } from 'lodash';

/**
 * Failure codes returned by b2c during login
 */
const LoginFailureCodes = {
    Cancelled: 'AADB2C90091', // User triggered cancellation
} as const;

export interface B2COptions {
    clientId: string;
    authorityDomain: string;
    authorityUrl: string;
    scopes: Record<string, string[]>;
    templateName: string;
}

/**
 * Authorization manager to communicate with Azure B2C
 */
export class B2CAuthorizationManager implements AuthorizationManager {
    public pca: PublicClientApplication;
    private scopes: Record<string, string[]>;
    private templateName: string;
    // Dictionary to track whether the token has been refreshed.
    // Used to ensure we have the right token loaded for all page reloads
    // in case the id token had been updated
    private tokenCacheRefreshTracker: Record<string, boolean> = {};
    private isInitialized = false;
    private initializationPromise: Promise<void> | null = null;

    constructor(options: B2COptions) {
        const { authorityDomain, authorityUrl, clientId, scopes, templateName } = options;
        this.scopes = scopes;
        this.templateName = templateName;
        this.pca = new PublicClientApplication({
            auth: {
                clientId,
                authority: authorityUrl,
                knownAuthorities: [authorityDomain],
                redirectUri: '/',
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false,
            },
        });

        this.initialize();
    }

    /**
     * Returns promise that will be resolved once b2c has finished initialization
     */
    public async waitForInitialization() {
        if (this.isInitialized) {
            return Promise.resolve(true);
        } else {
            await this.initializationPromise;
            return Promise.resolve(true);
        }
    }

    /**
     * Initialize the MSAL instance
     */
    private async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        // If initialization is already in progress, wait for it
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // Start initialization
        this.initializationPromise = this.performInitialization();
        return this.initializationPromise;
    }

    private async performInitialization(): Promise<void> {
        try {
            await this.pca.initialize();
            // Handle redirect promise and ensure active account is set properly
            const redirectResponse = await this.pca.handleRedirectPromise();
            if (!isNil(redirectResponse)) {
                const idTokenClaims = redirectResponse.idTokenClaims;
                const acr: unknown = get(idTokenClaims, 'acr');
                if (isString(acr)) {
                    const policy: string = acr.toLowerCase();
                    //If magic link push users back to login page to get correct signin policy
                    if (policy.includes('signup_magic_link')) {
                        await this.pca.logoutRedirect({
                            ...this.getAuthRedirectRequestOptions({ scopes: [] }),
                            postLogoutRedirectUri: '/',
                            authority: B2CCustomPolicyRoutes.SignIn,
                        });
                        return;
                    }
                } else {
                    this.pca.setActiveAccount(redirectResponse.account);
                }
            }

            this.addEvents();
            this.isInitialized = true;
        } catch (error) {
            this.initializationPromise = null;
            throw error;
        }
    }

    /**
     * Get an authenticated token
     * @returns An authorized token
     */
    public getAuthenticatedToken = async (url: string) => {
        await this.initialize();

        const account = this.getActiveAccount();

        if (account) {
            let requiresTokenRefresh = false;
            const cacheRefreshKey = `${account.localAccountId}-${url}`;
            if (this.tokenCacheRefreshTracker[cacheRefreshKey] !== true) {
                requiresTokenRefresh = true; // Require access token refresh since this is the first time we are loading the token
                this.tokenCacheRefreshTracker[cacheRefreshKey] = true;
            }

            const request: SilentRequest = {
                scopes: this.getScopes(url),
                account,
                forceRefresh: requiresTokenRefresh,
            };

            // TODO COPILOT-4887
            // This is currently required because we use a separate policy for signup
            // and we need to use the right policy to acquire access tokens
            const authority = this.getAuthority(account.homeAccountId);
            if (authority) request.authority = authority;

            try {
                const authResult = await this.pca.acquireTokenSilent(request);
                return authResult.accessToken;
            } catch (error) {
                console.log(error);
                return '';
            }
        } else {
            return '';
        }
    };

    /**
     * Returns the headers for authorizign a request
     * @returns Authorization Header
     */
    public getAuthorizationHeader = async (url: string) => {
        const token = await this.getAuthenticatedToken(url);
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    /**
     * Returns the currently logged in account
     * @returns The currently logged in account
     */
    private getActiveAccount = () => {
        const activeAccount = this.pca.getActiveAccount();
        const accounts = this.pca.getAllAccounts();

        // If the active account is already an impersonation account, use it
        if (
            activeAccount?.homeAccountId.includes('impersonation') ||
            activeAccount?.idTokenClaims?.iid
        ) {
            return activeAccount;
        }

        // Otherwise, look for any impersonation account in the cache
        const impersonationAccount = accounts.find(
            (account) =>
                account.homeAccountId.includes('impersonation') || account.idTokenClaims?.iid
        );

        return impersonationAccount ?? activeAccount ?? accounts[0];
    };

    /**
     * Get scopes for a url
     * @param url The url we want scopes for
     * @returns
     */
    private getScopes = (url: string): string[] => {
        return this.scopes[url] ?? [];
    };

    /**
     * Add events handler for pca instance
     */
    private addEvents = () => {
        this.pca.addEventCallback((event: EventMessage) => {
            switch (event.eventType) {
                case EventType.LOGIN_FAILURE: {
                    if (
                        event.error &&
                        event.error.message.indexOf(LoginFailureCodes.Cancelled) > -1
                    ) {
                        void this.pca.loginRedirect(
                            this.getAuthRedirectRequestOptions({
                                scopes: [],
                            })
                        );
                    }
                    break;
                }
                default:
                    break;
            }
        });
    };

    /**
     * Get the right authority policy for a given home account
     * @param homeAccountId The account we want to grab the policy for
     * @returns The authority or null
     */
    private getAuthority = (homeAccountId: string): string | null => {
        if (
            homeAccountId.includes('signup_invitation') ||
            homeAccountId.includes('signup_magic_link')
        )
            return B2CCustomPolicyRoutes.SignUp;
        else if (homeAccountId.includes('changetoken')) return B2CCustomPolicyRoutes.ChangeToken;
        else if (homeAccountId.includes('impersonation'))
            return B2CCustomPolicyRoutes.Impersonation;
        else return null;
    };

    /**
     * Merge Auth Redirect Options with required values
     * @param requestOptions Options we want to merge with
     * @returns Prepped Auth Redirect Options
     */
    private getAuthRedirectRequestOptions = (requestOptions: RedirectRequest) => {
        const { extraQueryParameters, ...rest } = requestOptions;
        return {
            ...rest,
            extraQueryParameters: {
                template: this.templateName,
                ...extraQueryParameters,
            },
        };
    };
}
